import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import useClickAway from 'react-use/lib/useClickAway';

import AsyncIcon from '@ux/icon/async-icon.js';

// Removed icon CSS imports; AsyncIcon handles icon rendering without per-icon CSS

import Logo from '@ux/logo';
import SidebarNav from './SidebarNav';
import SidebarFooter from './SidebarFooter';
import { useRootProps } from './UserDataProvider';

const shortEnv = require('@ux/environments')('short');

// eslint-disable-next-line complexity,
const Sidebar = ({
  navigation,
  logoHref,
  isAfternic,
  sidebarNavLinkHandler,
  primaryActiveNavLink,
  footerContent,
  sidebarNavTopComponent
}) => {
  const [navOpen, setNavOpen] = useState(false);
  const { env, logos, privateLabelId } = useRootProps();
  const envShort = shortEnv(env) || 'dev';

  const mobileNavigation = Array.isArray(navigation)
    ? navigation
      .filter(link => !link.desktopOnly)
      .map(link => ({
        ...link,
        children: link.children?.filter(child => !child.desktopOnly)
      }))
    : [];

  const openSidebar = useCallback((evt) => {
    if (evt) {
      evt.preventDefault();
    }
    setNavOpen(true);
  }, []);

  const closeSidebar = useCallback(() => {
    setNavOpen(false);
  }, []);

  const ref = useRef(null);

  useClickAway(ref, () => {
    closeSidebar();
  });

  return (<>
    <div data-testid='nav-mobile' className='sidebar-wrapper-mobile' ref={ ref }>
      <div className='logo-holder'>
        <button type='button' className='hamburger-wrap' onClick={ openSidebar }>
          <AsyncIcon icon='hamburger' />
        </button>
        <Logo
          className='logo-anchor'
          collapsedLogo
          logos={ logos }
          href={ envShort !== 'prod' && isAfternic ? `https://www.${envShort}-afternic.com` : logoHref }
          privateLabelId={ privateLabelId }
          figClass={ isAfternic ? 'afternic-logo' : '' }
        />
      </div>

      <div className={ classNames('sidebar-component-mobile', { 'mobile-open': navOpen }) }>
        <div className='top-mobile'>
          <Logo
            className='top-mobile-logo-anchor'
            collapsedLogo
            logos={ logos }
            href={ envShort !== 'prod' && isAfternic ? `https://www.${envShort}-afternic.com` : logoHref }
            privateLabelId={ privateLabelId }
            figClass={ isAfternic ? 'afternic-logo' : '' }
          />
          {/* TODO: Review with DF team on hw to better align this for accessibility */}
          {/* eslint-disable-next-line */}
          <a className='sidebar-nav-close' data-eid='app_sidebar.closebtn.link.click' onClick={ closeSidebar }>
            <span className='nav-link-icon'><AsyncIcon icon='x' /></span>
          </a>
        </div>

        { sidebarNavTopComponent && (
          <div className='mobile-sidebar-nav-top-component-wrapper'>
            { sidebarNavTopComponent }
          </div>
        )}

        <ul className='sidebar-links'>
          <SidebarNav
            navigation={ mobileNavigation }
            onClose={ closeSidebar }
            sidebarNavLinkHandler={ sidebarNavLinkHandler }
            primaryActiveNavLink={ primaryActiveNavLink }
          />
        </ul>
        { (Array.isArray(footerContent) && footerContent.length) ?
          <SidebarFooter sidebarNavLinkHandler={ sidebarNavLinkHandler } content={ footerContent } /> : null }
      </div>
    </div>
  </>);
};

Sidebar.propTypes = {
  logoHref: PropTypes.string,
  navigation: PropTypes.arrayOf(PropTypes.object),
  footerContent: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)),
  isAfternic: PropTypes.bool,
  sidebarNavLinkHandler: PropTypes.func,
  primaryActiveNavLink: PropTypes.string,
  sidebarNavTopComponent: PropTypes.node,
  showHubBanner: PropTypes.bool
};

export default Sidebar;
